import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import { Leader } from "@outschool/gql-backend-generated";
import {
  CurrentUserQueryQuery,
  TeacherHeader_LeaderFragment
} from "@outschool/gql-frontend-generated";
import { faEnvelope, faShare, faUser } from "@outschool/icons";
import {
  Trans,
  renderCurrencyString,
  useTranslation
} from "@outschool/localization";
import { addParamsToUrl, leaderPath, leaderUrl } from "@outschool/routes";
import {
  IntegrationCategory,
  measurePerformance,
  useTrackEvent
} from "@outschool/ui-analytics";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import {
  EducatorTierBadge,
  HelpLink,
  OutreachModal,
  OutreachModalType,
  SimpleRatings,
  SimpleVideo,
  useIsTeacherFollowedByCurrentUser
} from "@outschool/ui-components-website";
import { useIsMobile, useIsXLarge } from "@outschool/ui-utils";
import { Location } from "history";
import React, { useState } from "react";
import { useLocation } from "react-router";

import * as Routes from "../../../shared/Routes";
import Link from "../Link";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import PageModal from "../PageModal";
import { FollowButton } from "../saving/FollowButton";
import OutReachOrGive20Get20 from "../sharing/OutReachOrGive20Get20";
import VideoPlayer from "../VideoPlayer";

export default function TeacherHeader({
  leader,
  handleReviewsSummaryClick = () => {},
  showActionsInColumnForLargeScreens = false,
  includeProfileLink = false,
  includeShareButton = true
}: {
  leader: TeacherHeader_LeaderFragment;
  handleReviewsSummaryClick?: () => void;
  showActionsInColumnForLargeScreens?: boolean;
  includeProfileLink?: boolean;
  includeShareButton?: boolean;
}) {
  const isMobile = useIsMobile();
  const { currentUser, isAdmin, isLoggedIn } = useSession();
  const location = useLocation();
  const HeaderComponent = isMobile ? HeaderMobile : Header;

  return (
    <HeaderComponent
      leader={leader}
      currentUser={currentUser}
      handleReviewsSummaryClick={handleReviewsSummaryClick}
      isAdmin={isAdmin}
      isLoggedIn={isLoggedIn}
      location={location}
      showActionsInColumnForLargeScreens={showActionsInColumnForLargeScreens}
      includeProfileLink={includeProfileLink}
      includeShareButton={includeShareButton}
    />
  );
}

const LeaderActions = ({
  leader,
  location,
  currentUser,
  isLoggedIn,
  isAdmin,
  isVertical = false,
  includeProfileLink,
  includeShareButton
}: $TSFixMe) => {
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const isCurrentUserFollowingLeader =
    useIsTeacherFollowedByCurrentUser(leader);
  const { t } = useTranslation("client\\components\\teacher\\TeacherProfile");
  const editLink = currentUser && leader && currentUser.uid === leader.uid && (
    <Box
      sx={{
        marginTop: "16px",
        textAlign: "center"
      }}
    >
      <Link to={Routes.leaderSettingsPath()}>{t`Edit your profile`}</Link>
    </Box>
  );
  const leaderName = leader.name;
  const [showShareModal, setShowShareModal] = useState(false);
  const onlyCreditAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false
  });
  const title = t(
    "Share this teacher with a friend - new users will get {{onlyCreditAmount}} off",
    {
      onlyCreditAmount: onlyCreditAmount
    }
  );
  const subTitle = t(
    "You’ll also get {{onlyCreditAmount}} when they sign up with your link and take their first class!",
    {
      onlyCreditAmount: onlyCreditAmount
    }
  );
  const shareLink = addParamsToUrl(leaderUrl(leader.uid, leader.leader_link), {
    signup: true,
    usid: currentUser?.slug_id
  });

  const isEducator =
    !!currentUser?.teacher_approved_at && !currentUser?.suspended_at;

  return (
    <div>
      <Box
        flex
        sx={{
          flexDirection: "column"
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: isVertical ? "column" : "row",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: isVertical ? "stretch" : "start",
            columnGap: "16px",
            rowGap: isVertical ? "16px" : "8px"
          }}
        >
          {includeProfileLink && (
            <Button
              component={Link}
              size={isVertical ? "medium" : "small"}
              to={leaderPath(leader.uid, leader.leader_link)}
              startIcon={
                <Icon
                  icon={faUser}
                  size="small"
                  sx={{ fontSize: "14px !important" }}
                />
              }
            >
              {t("Profile")}
            </Button>
          )}{" "}
          <FollowButton
            size={isVertical ? "medium" : "small"}
            data-test-id="teacher-profile-follow-button"
            isSet={isCurrentUserFollowingLeader}
            leader={leader}
          />
          <MessageModal
            renderButton={(open: $TSFixMe) => (
              <Button
                variant="outlined"
                onClick={open}
                size={isVertical ? "medium" : "small"}
                startIcon={
                  <Icon
                    icon={faEnvelope}
                    size="small"
                    sx={{ fontSize: "14px !important" }}
                  />
                }
              >
                {t`Message`}
              </Button>
            )}
            user={currentUser}
            location={location}
            requireLogin={true}
            isLoggedIn={isLoggedIn}
            viewName="sendMessageToLeader"
            heading={t`Ask the teacher`}
            prompt={(close: $TSFixMe) => (
              <React.Fragment>
                <Trans t={t}>
                  Ask {{ leaderName }} about their classes or background. For
                  questions about the Outschool website,{" "}
                  <HelpLink onClick={close} sx={{ width: "fit-content" }}>
                    use Outschool Help
                  </HelpLink>
                  .
                </Trans>
              </React.Fragment>
            )}
            recipients={[leader]}
            onMessageCreated={() => {
              trackAdEvent("Message Teacher");
            }}
            showFromAndTo
            subject={t("Question for {{leaderName}}", { leaderName })}
            context={{
              action: t`Ask a Question`,
              leaderUid: leader.uid,
              leaderName: leader.name,
              subject: t("Question for {{leaderName}}", { leaderName })
            }}
          />
          {includeShareButton && (
            <>
              <Button
                color="primary"
                size={isVertical ? "medium" : "small"}
                variant="outlined"
                onClick={() => {
                  setShowShareModal(true);
                }}
                sx={{
                  marginBottom: 10
                }}
              >
                <Icon
                  icon={faShare}
                  sx={{
                    marginRight: "0.5em"
                  }}
                />{" "}
                {t("Share")}
              </Button>
              {isEducator ? (
                <OutreachModal
                  open={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  outreachShareUrl={shareLink}
                  modalType={{
                    variant: OutreachModalType.teacher,
                    teacher: leader
                  }}
                />
              ) : (
                <PageModal
                  open={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  fullBleed={false}
                  sx={{ maxWidth: 450, padding: "medium" }}
                >
                  <OutReachOrGive20Get20
                    title={title}
                    supportingCopy={subTitle}
                    link={shareLink}
                  />
                </PageModal>
              )}
            </>
          )}
        </Box>
        {editLink}

        {isAdmin && (
          <Box
            sx={{
              marginTop: "16px",
              textAlign: "center"
            }}
          >
            <Link to={Routes.userAdminPath(leader)}>
              <span role="img" aria-label="top hat">
                🎩
              </span>{" "}
              {t`Admin`}
            </Link>
          </Box>
        )}
      </Box>
    </div>
  );
};

LeaderActions.displayName = "LeaderActions";

interface PlayLeaderVideoOverlayButtonProps {
  // @ts-ignore TS(2339): Property 'leader' does not exist on type '{ __type... Remove this comment to see the full error message
  leader: TeacherPublicProfileQueryQuery["publicProfile"]["leader"];
  onClick?: () => void;
}

const PlayLeaderVideoOverlayButton = ({
  leader,
  onClick
}: PlayLeaderVideoOverlayButtonProps) => {
  const [showPlayButton, setShowPlayButton] = React.useState(true);
  const trackEvent = useTrackEvent();
  const { t } = useTranslation("client\\components\\teacher\\TeacherProfile");

  if (!leader.video || !showPlayButton) {
    return null;
  }

  const handlePlayClick = () => {
    trackEvent("Watched Leader Profile Video", {
      leaderUid: leader.uid
    });
    setShowPlayButton(false);
    onClick && onClick();
  };

  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        backgroundColor: "rgba(0, 0, 0, 0.01)",
        cursor: "pointer"
      }}
      aria-label={t`Play video with sound`}
      onClick={handlePlayClick}
    >
      &nbsp;
    </Box>
  );
};

interface HeaderProps {
  // @ts-ignore TS(2339): Property 'leader' does not exist on type '{ __type... Remove this comment to see the full error message
  leader: TeacherPublicProfileQueryQuery["publicProfile"]["leader"];
  currentUser: CurrentUserQueryQuery["currentUser"];
  isLoggedIn: boolean;
  isAdmin: boolean;
  location: Location;
  handleReviewsSummaryClick: () => void;
  showActionsInColumnForLargeScreens: boolean;
  includeProfileLink?: boolean;
  includeShareButton?: boolean;
}

function Header({
  leader,
  currentUser,
  isAdmin,
  isLoggedIn,
  handleReviewsSummaryClick,
  showActionsInColumnForLargeScreens,
  location,
  includeProfileLink,
  includeShareButton
}: HeaderProps) {
  const hasVideo = !!leader.video?.file.url;
  const isXLarge = useIsXLarge();
  const useActionColumn =
    (showActionsInColumnForLargeScreens && isXLarge) || !hasVideo;

  return (
    <Box
      flex
      sx={(theme: $TSFixMe) => ({
        backgroundColor: theme.palette.primary[50],
        padding: "48px",
        gap: "100px",
        justifyContent: "center",
        alignItems: "stretch"
      })}
    >
      <Box flex>
        <Box
          sx={{
            minHeight: "360px",
            maxWidth: "720px",
            backgroundColor: "white",
            borderRadius: "32px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "24px 48px",
            textAlign: "center",
            flexDirection: useActionColumn ? "row" : "column",
            gap: useActionColumn ? "48px" : "24px"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <UserHeadshotImage
              user={leader}
              size={useActionColumn ? 120 : 80}
              sx={{ marginBottom: "8px" }}
              onLoad={() => {
                measurePerformance("os-page-load", {
                  source: "image"
                });
              }}
            />
            <Typography
              variant="h5"
              sx={{
                wordBreak: "break-word"
              }}
            >
              {leader.name}
            </Typography>
            <Box sx={{ marginTop: "8px", marginBottom: "8px" }}>
              <RatingsAndTiers
                leader={leader}
                handleReviewsSummaryClick={handleReviewsSummaryClick}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "grey.500",
                fontSize: "16px",
                lineHeight: "20px",
                marginBottom: "8px"
              }}
            >
              {leader.details.headline}
            </Typography>
          </Box>
          <Box>
            <LeaderActions
              currentUser={currentUser}
              leader={leader}
              isLoggedIn={isLoggedIn}
              isAdmin={isAdmin}
              location={location}
              isVertical={useActionColumn}
              includeProfileLink={includeProfileLink}
              includeShareButton={includeShareButton}
            />
          </Box>
        </Box>
      </Box>
      {hasVideo ? (
        <Box
          sx={{
            position: "relative",
            justifySelf: "start",
            alignSelf: "center",
            borderRadius: "32px",
            overflow: "hidden"
          }}
        >
          <VideoPlayer
            sx={{
              width: "100%",
              maxHeight: 400
            }}
            videoSx={{ borderRadius: "32px", maxHeight: 400 }}
            src={leader.video?.file.url}
            playButtonType="white-center"
            poster={leader.video.file.thumbUrl || undefined}
            posterShadowSx={{ display: "none" }}
            autoPlay
            muted
          />
        </Box>
      ) : null}
    </Box>
  );
}

const HeaderMobile: React.FunctionComponent<HeaderProps> = ({
  leader,
  currentUser,
  isLoggedIn,
  isAdmin,
  location,
  handleReviewsSummaryClick,
  includeProfileLink,
  includeShareButton
}: HeaderProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const hasVideo = !!leader.video;

  React.useEffect(() => {
    // Autoplay doesn't work on physical mobile devices
    // this is a workaround to play the video on load
    videoRef.current && videoRef.current.play();
  }, []);

  return (
    <Box
      flex
      sx={{
        flexDirection: "column"
      }}
    >
      <Box
        flex
        sx={{
          marginTop: "16px",
          paddingRight: "24px",
          paddingLeft: "24px"
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: hasVideo ? "row" : "column",
            alignItems: "center",
            justifyContent: hasVideo ? "start" : "center"
          }}
        >
          <UserHeadshotImage
            user={leader}
            size={hasVideo ? 64 : 128}
            sx={{
              marginRight: hasVideo ? "12px" : 0,
              marginBottom: hasVideo ? 0 : "12px"
            }}
            onLoad={() => {
              measurePerformance("os-page-load", {
                source: "image"
              });
            }}
          />
          <Box
            flex
            sx={{
              flexDirection: "column"
            }}
          >
            <Typography
              variant="h6"
              sx={{
                marginBottom: "4px"
              }}
            >
              {leader.name}
            </Typography>
            <Box>
              <RatingsAndTiers
                leader={leader}
                handleReviewsSummaryClick={handleReviewsSummaryClick}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {hasVideo ? (
        <Box
          sx={{
            marginTop: "16px",
            width: "100%",
            height: "100%",
            position: "relative"
          }}
        >
          <SimpleVideo
            videoRef={videoRef}
            videoSx={{ objectFit: "contain" }}
            muted
            autoPlay
            onlyPlayButton
            showControls={false}
            src={leader.video.file.url}
            poster={leader.video.file.thumbUrl}
            onLoadedData={() => {
              measurePerformance("os-page-load", {
                source: "video"
              });
            }}
          />
          <PlayLeaderVideoOverlayButton
            leader={leader}
            onClick={() => {
              if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                videoRef.current.muted = false;
                videoRef.current.controls = true;
                videoRef.current.play();
              }
            }}
          />
        </Box>
      ) : null}
      <Box
        flex
        sx={{
          flexDirection: "column",
          marginTop: "24px",
          paddingRight: "24px",
          paddingLeft: "24px"
        }}
      >
        <Box
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "24px",
            textAlign: "center"
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "grey.500"
            }}
          >
            {leader.details.headline}
          </Typography>
        </Box>
      </Box>
      <Box>
        <LeaderActions
          leader={leader}
          currentUser={currentUser}
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          location={location}
          includeProfileLink={includeProfileLink}
          includeShareButton={includeShareButton}
        />
      </Box>
    </Box>
  );
};

function RatingsAndTiers({
  leader,
  handleReviewsSummaryClick
}: {
  leader: Leader;
  handleReviewsSummaryClick: () => void;
}) {
  return (
    <Box flex sx={{ gap: "10px", flexWrap: "wrap" }}>
      {leader.educatorTiers?.map(tier => (
        <EducatorTierBadge key={tier} tier={tier} />
      ))}
      {leader.reviewCount > 0 && leader.averageActivityStarRating !== null && (
        <Box
          flex
          sx={{
            alignItems: "center",
            cursor: "pointer"
          }}
          onClick={handleReviewsSummaryClick}
        >
          <SimpleRatings
            reviewCount={leader.reviewCount}
            starRating={leader.averageActivityStarRating}
          />
        </Box>
      )}
    </Box>
  );
}

TeacherHeader.fragments = {
  activity: gql`
    fragment TeacherHeader_leader on Leader {
      uid
      reviewCount
      averageActivityStarRating
      educatorTiers
      name
      leader_link
      photo
      video {
        file {
          url
          thumbUrl
        }
      }
      details {
        headline
      }
    }
  `
};
